import React from "react";
import styles from "../PaymentDetailPage.module.css";
import { money, getOrderCode } from "./helpers";

export default function EditAllocationModal({
  mode, draft, setDraft, selectedAllocation, newFiles, setNewFiles,
  clientOrders, currencies, currenciesById, paymentCurrency,
  modalError, saving, onClose, onSave, onDelete,
}) {
  if (!mode) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {mode === "delete" ? (
          <>
            <h2 className="text-xl font-bold text-slate-900">Удалить клиентское назначение?</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Заказ: <strong>{selectedAllocation?.order_code}</strong>
              <br />
              Сумма:{" "}
              <strong>
                {money(selectedAllocation?.amount, currenciesById[selectedAllocation?.currency] || paymentCurrency)}
              </strong>
            </p>
            <p className="mt-3 text-sm text-red-600">
              Вместе с клиентским назначением будут удалены связанные наши назначения.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-slate-900">Редактировать распределение платежа</h2>
            <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Код заказа</label>
                <input
                  list="edit-order-suggestions"
                  value={draft.orderCode}
                  onChange={(e) => setDraft((c) => ({ ...c, orderCode: e.target.value }))}
                  autoComplete="off"
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                />
                <datalist id="edit-order-suggestions">
                  {clientOrders.map((order) => (
                    <option
                      key={order.id}
                      value={getOrderCode(order)}
                      label={[getOrderCode(order), order._clientName || ""].filter(Boolean).join(" — ")}
                    />
                  ))}
                </datalist>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700">Сумма</label>
                  <input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0.01"
                    step="0.01"
                    value={draft.amount}
                    onChange={(e) => setDraft((c) => ({ ...c, amount: e.target.value }))}
                    required
                    className={`${styles.noNumberSpinner} w-full rounded-lg border border-slate-300 px-3 py-2.5`}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Валюта</label>
                  <select
                    value={draft.currency || ""}
                    onChange={(e) => setDraft((c) => ({ ...c, currency: e.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                  >
                    <option value="">—</option>
                    {currencies.map((c) => (
                      <option key={c.id} value={c.id}>{c.symbol} {c.code}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Дата</label>
                <input
                  type="date"
                  value={draft.date || ""}
                  onChange={(e) => setDraft((c) => ({ ...c, date: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Платёж через</label>
                <input
                  value={draft.payment_agent || ""}
                  onChange={(e) => setDraft((c) => ({ ...c, payment_agent: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Статус</label>
                <select
                  value={draft.payment_status || "PENDING"}
                  onChange={(e) => setDraft((c) => ({ ...c, payment_status: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                >
                  <option value="PENDING">Ожидает</option>
                  <option value="PAID">Оплачен</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Комментарий</label>
                <textarea
                  rows={3}
                  value={draft.comment || ""}
                  onChange={(e) => setDraft((c) => ({ ...c, comment: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Добавить документы</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setNewFiles(Array.from(e.target.files || []))}
                  className="block w-full text-sm"
                />
                {newFiles.length > 0 && (
                  <ul className="mt-1 text-xs text-slate-600">
                    {newFiles.map((f, i) => <li key={i}>{f.name}</li>)}
                  </ul>
                )}
              </div>
            </form>
          </>
        )}

        {modalError && (
          <div className="mt-4 whitespace-pre-wrap rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {modalError}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Отмена
          </button>
          {mode === "delete" ? (
            <button
              type="button"
              disabled={saving}
              onClick={onDelete}
              className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? "Удаление…" : "Удалить"}
            </button>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={onSave}
              className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Сохранение…" : "Сохранить"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}